import React, { useEffect, useState, useCallback } from 'react';
import { Package, AlertTriangle, Plus, Minus, Save, Boxes } from 'lucide-react';
import api from '@/lib/api';
import { KATEGORI, KATEGORI_LABEL } from '@/lib/format';
import { ListSkeleton } from '@/components/Skeletons';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function Inventory() {
  const [data, setData] = useState({ items: [], low_count: 0 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('profil');
  const [editItem, setEditItem] = useState(null);
  const [minVal, setMinVal] = useState('');
  const [stokVal, setStokVal] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/inventory'); setData(r.data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const adjust = async (item, delta) => {
    try {
      await api.patch(`/inventory/${item.id}`, { delta });
      load();
    } catch { toast.error('Gagal'); }
  };

  const openEdit = (it) => { setEditItem(it); setMinVal(it.stok_min); setStokVal(it.stok); };
  const saveEdit = async () => {
    try {
      await api.patch(`/inventory/${editItem.id}`, { stok: Number(stokVal), stok_min: Number(minVal) });
      toast.success('Stok diperbarui'); setEditItem(null); load();
    } catch { toast.error('Gagal menyimpan'); }
  };

  const byKat = (k) => data.items.filter((i) => i.kategori === k);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-xl font-bold"><Boxes className="h-5 w-5 text-primary" /> Inventory / Stok</h1>
        <p className="text-sm text-muted-foreground">Pantau stok bahan & alat untuk pengingat belanja.</p>
      </div>

      {data.low_count > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800">
          <AlertTriangle className="h-4 w-4" /> <b>{data.low_count} item</b> stok menipis — perlu segera dibeli.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-4">
          {KATEGORI.map((k) => <TabsTrigger key={k.value} value={k.value} className="text-xs">{k.label.split(' ')[0]}</TabsTrigger>)}
        </TabsList>
        {KATEGORI.map((k) => (
          <TabsContent key={k.value} value={k.value} className="mt-3">
            {loading ? <ListSkeleton /> : byKat(k.value).length === 0 ? (
              <EmptyState icon={Package} title={`Belum ada ${k.label}`} description="Tambah item di Katalog & Harga dulu." />
            ) : (
              <div className="space-y-2.5">
                {byKat(k.value).map((it) => (
                  <div key={it.id} className={cn('rounded-xl border bg-card p-3.5', it.low && 'border-orange-300 bg-orange-50/40')}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2"><span className="truncate font-semibold">{it.nama}</span>{it.low && <Badge variant="outline" className="rounded-full border-orange-300 bg-orange-100 text-orange-800">Stok menipis</Badge>}</div>
                        <div className="text-xs text-muted-foreground">Min: {it.stok_min} {it.satuan} · {[it.merek, it.warna].filter(Boolean).join(' ')}</div>
                      </div>
                      <button onClick={() => openEdit(it)} className="text-xs text-primary">Atur</button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button onClick={() => adjust(it, -1)} className="flex h-9 w-9 items-center justify-center rounded-lg border hover:bg-muted"><Minus className="h-4 w-4" /></button>
                        <span className="min-w-[3rem] text-center font-display text-lg font-bold tabular-nums">{it.stok}</span>
                        <button data-testid="inventory-tambah-stok" onClick={() => adjust(it, 1)} className="flex h-9 w-9 items-center justify-center rounded-lg border hover:bg-muted"><Plus className="h-4 w-4" /></button>
                        <span className="text-xs text-muted-foreground">{it.satuan}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Atur Stok — {editItem?.nama}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Stok Saat Ini</Label><Input type="number" value={stokVal} onChange={(e) => setStokVal(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Stok Minimum</Label><Input type="number" value={minVal} onChange={(e) => setMinVal(e.target.value)} /></div>
          </div>
          <DialogFooter><Button onClick={saveEdit} className="rounded-xl"><Save className="mr-2 h-4 w-4" /> Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
